import { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { web, errMsg } from "../../lib/api";
import { Btn, Loader } from "./kit";
import { Modal, Field, TextInput } from "./Form";

const blankQ = () => ({ text: "", choices: ["", ""], answer: 0 });

const QuestionEdit = ({ q, i, onChange, onRemove, canRemove }) => {
  const setChoice = (c, v) => onChange({ ...q, choices: q.choices.map((x, k) => (k === c ? v : x)) });
  const dropChoice = (c) => onChange({ ...q, choices: q.choices.filter((_, k) => k !== c), answer: q.answer === c ? 0 : q.answer > c ? q.answer - 1 : q.answer });
  return (
    <li data-testid={`quiz-q-${i}`} className="space-y-2 rounded-xl border border-[#1E293B] bg-[#0F172A] p-3">
      <div className="flex items-center gap-2">
        <span className="font-mono-gd text-xs text-[#39FF14]">Q{i + 1}</span>
        <TextInput data-testid={`quiz-q-${i}-text`} value={q.text} onChange={(e) => onChange({ ...q, text: e.target.value })} placeholder="Where does the 4 man go on the flare?" className="py-2" />
        {canRemove && <button type="button" data-testid={`quiz-q-${i}-remove`} onClick={onRemove} className="rounded-full p-2 text-slate-400 hover:text-red-300" aria-label="Remove question"><Trash2 className="h-4 w-4" /></button>}
      </div>
      {q.choices.map((c, k) => (
        <div key={k} className="flex items-center gap-2 pl-6">
          <button type="button" data-testid={`quiz-q-${i}-correct-${k}`} onClick={() => onChange({ ...q, answer: k })} aria-label="Mark correct"
            className={`h-5 w-5 shrink-0 rounded-full border-2 transition-colors ${q.answer === k ? "border-[#39FF14] bg-[#39FF14]" : "border-slate-500 hover:border-[#39FF14]"}`} />
          <TextInput data-testid={`quiz-q-${i}-choice-${k}`} value={c} onChange={(e) => setChoice(k, e.target.value)} placeholder={`Answer ${k + 1}`} className="py-2" />
          {q.choices.length > 2 && <button type="button" onClick={() => dropChoice(k)} data-testid={`quiz-q-${i}-drop-${k}`} className="p-1 text-slate-400 hover:text-red-300" aria-label="Remove answer"><X className="h-4 w-4" /></button>}
        </div>
      ))}
      {q.choices.length < 4 && <button type="button" data-testid={`quiz-q-${i}-add-choice`} onClick={() => onChange({ ...q, choices: [...q.choices, ""] })} className="pl-6 text-xs font-semibold text-[#39FF14] hover:underline">+ Add answer</button>}
    </li>
  );
};

/** Coach: create / edit / delete the quiz on a play. Green dot marks the right answer. */
export const QuizEditor = ({ open, onClose, tid, play, onSaved }) => {
  const [state, setState] = useState({ loading: true, exists: false });
  const [pass, setPass] = useState(80);
  const [qs, setQs] = useState([blankQ()]);
  const [busy, setBusy] = useState(false);
  const path = play && `/teams/${tid}/plays/${play.id}/quiz`;

  useEffect(() => {
    if (!open || !path) return undefined;
    let live = true;
    setState({ loading: true, exists: false });
    web.get(path).then(({ data }) => {
      if (!live) return;
      const q = data?.quiz;
      setPass(q?.pass_pct ?? 80);
      setQs(Array.isArray(q?.questions) && q.questions.length ? q.questions.map((x) => ({ text: x.text, choices: [...x.choices], answer: x.answer ?? 0 })) : [blankQ()]);
      setState({ loading: false, exists: !!q });
    }).catch((e) => { if (live) { toast.error(errMsg(e)); setState({ loading: false, exists: false }); } });
    return () => { live = false; };
  }, [open, path]);

  const save = async (e) => {
    e.preventDefault();
    if (qs.some((q) => !q.text.trim() || q.choices.some((c) => !c.trim()))) return toast.error("Fill in every question and answer");
    setBusy(true);
    try {
      const { data } = await web.put(path, { pass_pct: Number(pass) || 80, questions: qs });
      toast.success(data?.reset ? "Quiz updated. Players retake the new questions." : "Quiz saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this quiz and all player results?")) return;
    try { await web.delete(path); toast.success("Quiz deleted"); onSaved(); onClose(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Modal open={open} onClose={onClose} testid="quiz-editor" title={state.exists ? "Edit quiz" : "Add quiz"} description={`${play?.title ?? ""} · Tap the circle next to the right answer.`}>
      {state.loading ? <Loader /> : (
        <form onSubmit={save} className="mt-2 space-y-4">
          <Field label="Passing score (%)"><TextInput data-testid="quiz-pass-input" type="number" min={1} max={100} value={pass} onChange={(e) => setPass(e.target.value)} className="w-28" /></Field>
          <ol className="space-y-3">
            {qs.map((q, i) => <QuestionEdit key={i} q={q} i={i} canRemove={qs.length > 1} onChange={(nq) => setQs(qs.map((x, k) => (k === i ? nq : x)))} onRemove={() => setQs(qs.filter((_, k) => k !== i))} />)}
          </ol>
          {qs.length < 20 && <Btn type="button" variant="ghost" onClick={() => setQs([...qs, blankQ()])} data-testid="quiz-add-question" className="px-4 py-2 text-xs"><Plus className="h-3.5 w-3.5" />Add question</Btn>}
          {state.exists && <p className="text-xs text-slate-400">Changing questions or answers clears earlier results so everyone retakes the new version.</p>}
          <div className="flex flex-wrap justify-between gap-2 pt-2">
            {state.exists ? <Btn type="button" variant="danger" onClick={remove} data-testid="quiz-delete"><Trash2 className="h-4 w-4" />Delete quiz</Btn> : <span />}
            <div className="flex gap-2">
              <Btn type="button" variant="ghost" onClick={onClose} data-testid="quiz-editor-cancel">Cancel</Btn>
              <Btn type="submit" disabled={busy} data-testid="quiz-save">{busy ? "Saving…" : "Save quiz"}</Btn>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
};
